import React, { useEffect, useRef, useState } from 'react';
import { LoadScript, GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { FaPhone, FaEnvelope } from 'react-icons/fa';

const libraries = ['places'];

const GoogleMapComponent = ({ offices = [], selectedOffice }) => {
    const mapRef = useRef(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [currentSelectedOffice, setCurrentSelectedOffice] = useState(null);
    const googleMapsApiKey = process.env.GATSBY_GOOGLE_MAPS_API_KEY;

    useEffect(() => {
        if (!googleMapsApiKey) {
            console.error('Google Maps API key is missing');
        }
    }, [googleMapsApiKey]);

    useEffect(() => {
        if (selectedOffice) {
            setCurrentSelectedOffice(selectedOffice);
            if (mapRef.current) {
                mapRef.current.panTo(selectedOffice.location);
            }
        }
    }, [selectedOffice]);

        const mapStyles = [
        {
            "elementType": "geometry",
            "stylers": [
                {
                    "color": "#f5f5f5"
                }
            ]
        },
        {
            "elementType": "labels.icon",
            "stylers": [
                {
                    "visibility": "off"
                }
            ]
        },
        {
            "elementType": "labels.text.fill",
            "stylers": [
                {
                    "color": "#616161"
                }
            ]
        },
        {
            "elementType": "labels.text.stroke",
            "stylers": [
                {
                    "color": "#f5f5f5"
                }
            ]
        },
        {
            "featureType": "administrative.land_parcel",
            "elementType": "labels.text.fill",
            "stylers": [
                {
                    "color": "#bdbdbd"
                }
            ]
        },
        {
            "featureType": "poi",
            "elementType": "geometry",
            "stylers": [
                {
                    "color": "#eeeeee"
                }
            ]
        },
        {
            "featureType": "poi",
            "elementType": "labels.text.fill",
            "stylers": [
                {
                    "color": "#757575"
                }
            ]
        },
        {
            "featureType": "poi.park",
            "elementType": "geometry",
            "stylers": [
                {
                    "color": "#e5e5e5"
                }
            ]
        },
        {
            "featureType": "poi.park",
            "elementType": "labels.text.fill",
            "stylers": [
                {
                    "color": "#9e9e9e"
                }
            ]
        },
        {
            "featureType": "road",
            "elementType": "geometry",
            "stylers": [
                {
                    "color": "#ffffff"
                }
            ]
        },
        {
            "featureType": "road.arterial",
            "elementType": "labels.text.fill",
            "stylers": [
                {
                    "color": "#757575"
                }
            ]
        },
        {
            "featureType": "road.highway",
            "elementType": "geometry",
            "stylers": [
                {
                    "color": "#dadada"
                }
            ]
        },
        {
            "featureType": "road.highway",
            "elementType": "labels.text.fill",
            "stylers": [
                {
                    "color": "#616161"
                }
            ]
        },
        {
            "featureType": "road.local",
            "elementType": "labels.text.fill",
            "stylers": [
                {
                    "color": "#9e9e9e"
                }
            ]
        },
        {
            "featureType": "transit.line",
            "elementType": "geometry",
            "stylers": [
                {
                    "color": "#e5e5e5"
                }
            ]
        },
        {
            "featureType": "transit.station",
            "elementType": "geometry",
            "stylers": [
                {
                    "color": "#eeeeee"
                }
            ]
        },
        {
            "featureType": "water",
            "elementType": "geometry",
            "stylers": [
                {
                    "color": "#c9c9c9"
                }
            ]
        },
        {
            "featureType": "water",
            "elementType": "labels.text.fill",
            "stylers": [
                {
                    "color": "#9e9e9e"
                }
            ]
        }
    ];
    const defaultCenter = {
        lat: 24.7436,
        lng: 46.6753
    };

    const handleLoad = (map) => {
        setIsLoaded(true);
        mapRef.current = map;
    };

    const handleError = (error) => {
        console.error("Error loading Google Maps API:", error);
    };

    return (
        <LoadScript
            googleMapsApiKey={googleMapsApiKey}
            libraries={libraries}
            onLoad={handleLoad}
            onError={handleError}
        >
            {isLoaded ? (
                <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '500px' }}
                    center={defaultCenter}
                    zoom={12}
                    options={{ styles: mapStyles }}
                    onLoad={handleLoad}
                >
                    {offices.length > 0 && offices.map((office, index) => (
                        <Marker
                            key={index}
                            position={office.location}
                            onClick={() => {
                                setCurrentSelectedOffice(office);
                            }}
                        />
                    ))}

                    {currentSelectedOffice && (
                        <InfoWindow
                            position={currentSelectedOffice.location}
                            onCloseClick={() => {
                                setCurrentSelectedOffice(null);
                            }}
                        >
                            <div className="p-4 lg:pt-8 pb-8 relative">
                                <div className="lg:flex w-full">
                                    <div className="lg:w-1/2 px-2">
                                        <img src={currentSelectedOffice.image} alt={currentSelectedOffice.name} className="w-full h-auto" />
                                    </div>
                                    <div className="lg:w-1/2 pl-2">
                                        <h2 className="mt-4 lg:mt-0 text-lg font-bold">{currentSelectedOffice.name}, {currentSelectedOffice.country}</h2>
                                        <p>{currentSelectedOffice.address}</p>
                                        <p className="flex items-center">
                                            <FaPhone className="mr-2" />
                                            <a target="_blank" rel="noreferrer noopener" href={`tel:${currentSelectedOffice.phone}`} className="hover:underline">
                                                {currentSelectedOffice.phone}
                                            </a>
                                        </p>
                                        <p className="flex items-center">
                                            <FaEnvelope className="mr-2" />
                                            <a target="_blank" rel="noreferrer noopener" href={`mailto:${currentSelectedOffice.email}`} className="hover:underline">
                                                {currentSelectedOffice.email}
                                            </a>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </InfoWindow>
                    )}
                </GoogleMap>
            ) : (
                <div>Loading...</div>
            )}
        </LoadScript>
    );
};

export default GoogleMapComponent;